const ANCHOR_TIMEOUT_MS = 4000;
export const SETTLE_INTERVAL_MS = 50;
const SETTLE_QUIET_MS = 150;
const SETTLE_TIMEOUT_MS = 2000;

export function asList(value: string | string[] | undefined): string[] {
  if (value === undefined) return [];
  return Array.isArray(value) ? value : [value];
}

// Safari before 17.4 has no checkVisibility. Its default check is
// display: none on the element or an ancestor, which is how v-show hides.
export function isShown(element: Element): boolean {
  if (typeof element.checkVisibility === "function") {
    return element.checkVisibility();
  }
  for (let at: Element | null = element; at; at = at.parentElement) {
    if (getComputedStyle(at).display === "none") return false;
  }
  return true;
}

// Fallback anchors are tried in the order given, not in page order. Hidden
// copies (v-show panels, desktop/mobile duplicates) don't count.
export function findAnchor(anchor: string | string[]): Element | null {
  for (const name of Array.isArray(anchor) ? anchor : [anchor]) {
    const visible = Array.from(
      document.querySelectorAll(`[data-tour="${name}"]`),
    ).find(isShown);
    if (visible) return visible;
  }
  return null;
}

export function waitForAnchor(
  anchor: string | string[],
  timeoutMs = ANCHOR_TIMEOUT_MS,
): Promise<Element | null> {
  const existing = findAnchor(anchor);
  if (existing) return Promise.resolve(existing);
  return new Promise((resolve) => {
    const observer = new MutationObserver(() => {
      const found = findAnchor(anchor);
      if (found) {
        observer.disconnect();
        clearTimeout(timer);
        resolve(found);
      }
    });
    const timer = setTimeout(() => {
      observer.disconnect();
      resolve(null);
    }, timeoutMs);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["style", "class"],
    });
  });
}

// Its own animations say exactly when it lands, so no stillness check needed.
function runningAnimationsAround(element: Element): Animation[] {
  if (typeof document.getAnimations !== "function") return [];
  return document.getAnimations().filter((animation) => {
    const target = (animation.effect as KeyframeEffect | null)?.target;
    return target instanceof Element && target.contains(element);
  });
}

export function nextFrame(): Promise<void> {
  return new Promise((resolve) => requestAnimationFrame(() => resolve()));
}

// driver.js scrolls once, so highlighting before the sections above have
// loaded leaves the target below the fold.
export async function waitForStableLayout(element: Element): Promise<void> {
  // Enter transitions start a frame or two after the element is inserted.
  await nextFrame();
  await nextFrame();
  const animations = runningAnimationsAround(element);
  if (animations.length > 0) {
    await Promise.race([
      Promise.allSettled(animations.map((animation) => animation.finished)),
      new Promise((resolve) => setTimeout(resolve, SETTLE_TIMEOUT_MS)),
    ]);
    return;
  }
  return new Promise((resolve) => {
    function position() {
      const rect = element.getBoundingClientRect();
      return `${rect.top},${rect.left}`;
    }
    let last = position();
    let quietFor = 0;
    let elapsed = 0;
    const timer = setInterval(() => {
      const current = position();
      quietFor = current === last ? quietFor + SETTLE_INTERVAL_MS : 0;
      last = current;
      elapsed += SETTLE_INTERVAL_MS;
      if (quietFor >= SETTLE_QUIET_MS || elapsed >= SETTLE_TIMEOUT_MS) {
        clearInterval(timer);
        resolve();
      }
    }, SETTLE_INTERVAL_MS);
  });
}
